import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, inject, Input, Output, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faClock, faTrashCan, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

import { ConfirmationModalComponent } from "../../../../../shared-components/confirmation-modal/confirmation-modal.component";
import { QuizAdministratorView } from '../../../../../models/views/quiz-administrator-view.interface';
import { EnrollmentState } from '../../../../../models/enrollment-state.enum';

@Component({
    selector: 'app-administrator-actions',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FontAwesomeModule, ConfirmationModalComponent],
    styleUrl: './administrator-actions.component.scss',
    template: `
    @if (!isOwner && !isPending) {
      @if (deletingAdminEmail === enrollment.email) {
        <div data-testid="admin-deletion-spinner" class="ms-2 mt-2 text-primary spinner-border spinner-border-sm" role="status">
          <span class="visually-hidden">Removing administrator...</span>
        </div>
      } @else {
        <app-confirmation-modal
          (isAffirmative)="removeAdministrator()"
          [triggerButtonColor]="'transparent'"
        >
          <modal-header>Remove administrator access?</modal-header>
          <modal-body class="d-flex">
            <fa-icon class="text-warning fs-2" [icon]="triangleExclamation"></fa-icon>
            <span class="d-flex align-items-center ps-3">Are you sure you want to remove {{ enrollment.firstName }} {{ enrollment.lastName }} as an administrator?</span>
          </modal-body>
          <modal-affirmative>Remove access</modal-affirmative>
          <modal-negative>Cancel</modal-negative>
          <modal-trigger-inner>
            <fa-icon class="text-danger" [icon]="trashIcon"></fa-icon>
          </modal-trigger-inner>
        </app-confirmation-modal>
        @if (isInvited()) {
          <span class="badge rounded-pill bg-light text-black fw-light">
            <fa-icon [icon]="faClock" class="me-1 text-secondary"></fa-icon>
            Invite Sent
          </span>
        }
      }
    }
    @else {
      @if (isOwner) {
        <span class="badge rounded-pill bg-success text-light fw-light">Owner</span>
      }
      @if (isPending && !isOwner) {
        @if (deletingAdminEmail === enrollment.email) {
          <div data-testid="admin-deletion-spinner" class="spinner-border spinner-border-sm text-danger" role="status">
            <span class="visually-hidden">Removing administrator...</span>
          </div>
        } @else {
          <button (click)="removeAdministrator()" class="btn" type="button">
            <fa-icon class="text-danger" [icon]="trashIcon"></fa-icon>
            <span class="visually-hidden">Remove administrator</span>
          </button>
        }
      }
    }
  `
})
export class AdministratorActionsComponent {
  @Input() enrollment!: QuizAdministratorView;
  @Input() isOwner!: boolean;
  @Input() isPending: boolean = false;
  @Input() deletingAdminEmail: string | null = null;
  @Output() administratorRemoved = new EventEmitter<string>();

  trashIcon = faTrashCan;
  triangleExclamation = faTriangleExclamation;
  faClock = faClock;

  removeAdministrator() {
    this.administratorRemoved.emit(this.enrollment.email);
  }

  isInvited() {
    return this.enrollment.status == EnrollmentState.Invited;
  }
}
